import { useCallback, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { CardAppearTransition } from './transitions';
import { Box, Image } from 'v1/ui';

const transitionTime = 400;
const styles = {
  transition: {
    transition: `opacity ${transitionTime}ms ease-out, transform ${transitionTime}ms ease-out`,
  },
  transitionTitleOut: {
    transition: `opacity ${
      transitionTime / 2
    }ms ease-out, transform ${transitionTime}ms ease-out`,
  },
  transitionTitleIn: {
    transition: `opacity ${transitionTime}ms ease-in, transform ${transitionTime}ms ease-out`,
  },
  title: {
    visible: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    hidden: {
      opacity: 0,
      transform: 'translateY(-100%)',
    },
  },
  text: {
    visible: {
      opacity: 1,
      transform: 'translateY(0)',
    },
    hidden: {
      opacity: 0,
      transform: 'translateY(120%)',
    },
  },
};

export type CardData = {
  bgColor?: string;
  bgImgId?: string;
  imgId?: string;
  text?: string;
  textColor: string;
  title: string;
};

export type CardProps = CardData & { index: number };

export default function Card(props: CardProps) {
  const { bgColor, bgImgId, imgId, index, text, textColor, title } = props;
  const [hovered, setHovered] = useState(false);

  const mouseEnter = useCallback(() => {
    if (text) setHovered(true);
  }, [text]);

  const mouseLeave = useCallback(() => {
    if (text) setHovered(false);
  }, [text]);

  return (
    <CardAppearTransition index={index}>
      <Box
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseLeave}
        sx={{
          width: '100%',
          backgroundColor: bgColor ? bgColor : undefined,
          backgroundImage: bgImgId ? `url(${getUrl(bgImgId)})` : undefined,
          backgroundSize: 'cover',
          mb: 5,
          overflow: 'hidden',
        }}
      >
        {imgId && <CardImage imgId={imgId} />}
        <Box
          sx={{
            position: 'relative',
            mx: 24,
            pt: 24,
          }}
        >
          <CardTitleTransition active={!hovered}>
            <Box
              sx={{
                ...styles.title.visible,
                color: textColor,
                fontSize: 32,
                fontWeight: 600,
                lineHeight: 1.25,
                pb: 5,
              }}
            >
              {title}
            </Box>
          </CardTitleTransition>
          <CardTextTransition active={hovered}>
            <Box
              variant="text.baseEmphasis"
              sx={{
                ...styles.text.hidden,
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                color: textColor,
                pb: 5,
              }}
            >
              {text}
            </Box>
          </CardTextTransition>
        </Box>
      </Box>
    </CardAppearTransition>
  );
}

function CardImage({ imgId }: { imgId: string }) {
  return (
    <Image
      src={getUrl(imgId)}
      sx={{
        width: '100%',
        height: 'auto',
        mt: 5,
      }}
    />
  );
}

function getUrl(id: string): string {
  return `https://assets.standardresume.co/q_auto,f_auto,c_fill,w_620/v1/app/upgrade/${id}`;
}

function CardTitleTransition({
  children,
  active,
}: {
  children: React.ReactNode;
  active: boolean;
}) {
  return (
    <Box
      sx={{
        '.card-title.enter-active': {
          ...styles.title.hidden,
        },
        '.card-title.enter-done': {
          ...styles.title.visible,
          ...styles.transitionTitleIn,
        },
        '.card-title.exit-active': {
          ...styles.title.visible,
        },
        '.card-title.exit-done': {
          ...styles.title.hidden,
          ...styles.transitionTitleOut,
        },
      }}
    >
      <CSSTransition
        appear={true}
        in={active}
        className="card-title"
        timeout={0}
      >
        {children}
      </CSSTransition>
    </Box>
  );
}

function CardTextTransition({
  children,
  active,
}: {
  children: React.ReactNode;
  active: boolean;
}) {
  return (
    <Box
      sx={{
        '.card-text.enter-active': {
          ...styles.text.hidden,
        },
        '.card-text.enter-done': {
          ...styles.text.visible,
          ...styles.transition,
        },
        '.card-text.exit-active': {
          ...styles.text.visible,
        },
        '.card-text.exit-done': {
          ...styles.text.hidden,
          ...styles.transition,
        },
      }}
    >
      <CSSTransition
        appear={true}
        in={active}
        className="card-text"
        timeout={0}
      >
        {children}
      </CSSTransition>
    </Box>
  );
}
